import type { CookiePreferences } from '@/types/cookies';

let initialized = false;

// Simple guards so we don't double-load vendors
let analyticsActive = false;
let marketingActive = false;

function gtagUpdate(consents: Record<string, 'granted' | 'denied'>) {
  try {
    // window.gtag is defined in index.html before GA script loads; it queues into dataLayer
    (window as any).gtag('consent', 'update', consents);
  } catch {
    // no-op
  }
}

function ensureGaScript() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('ga4-script')) return; // already injected
  const id = 'G-0LS2SF3BYG'; // measurement ID (move to env if needed)
  const s = document.createElement('script');
  s.id = 'ga4-script';
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  s.onload = () => {
    try {
      (window as any).gtag('js', new Date());
      (window as any).gtag('config', id, { send_page_view: false });
    } catch {/* ignore */}
  };
  document.head.appendChild(s);
}

function enableAnalytics() {
  if (analyticsActive) return;
  analyticsActive = true;
  ensureGaScript();
  // Update GA4 consent to allow analytics storage
  gtagUpdate({ 'analytics_storage': 'granted' });
  console.info('[consent] Analytics enabled');
}

function disableAnalytics() {
  if (!analyticsActive) return;
  analyticsActive = false;
  // Revoke analytics storage
  gtagUpdate({ 'analytics_storage': 'denied' });
  // Optional: remove script to stop further network requests (existing queued events denied by consent)
  try {
    const el = document.getElementById('ga4-script');
    if (el && el.parentNode) el.parentNode.removeChild(el);
    // Clear gtag function to prevent accidental sends
    (window as any).gtag = function(){ /* discarded after revoke */ };
  } catch {/* ignore */}
  console.info('[consent] Analytics disabled');
}

function enableMarketing() {
  if (marketingActive) return;
  marketingActive = true;
  // Allow ads-related storage and personalization
  gtagUpdate({ 'ad_storage': 'granted', 'ad_user_data': 'granted', 'ad_personalization': 'granted' });
  console.info('[consent] Marketing enabled');
}

function disableMarketing() {
  if (!marketingActive) return;
  marketingActive = false;
  // Deny ads-related storage and personalization
  gtagUpdate({ 'ad_storage': 'denied', 'ad_user_data': 'denied', 'ad_personalization': 'denied' });
  console.info('[consent] Marketing disabled');
}

function applyConsent(prefs: CookiePreferences | null | undefined) {
  if (!prefs) return; // Nothing to apply yet
  // Expose a simple global boolean so other modules can gate sends without importing React context
  try { (window as any).__ts_analytics_granted = !!prefs.analytics; } catch {}
  // Functionality/storage for features (maps to functionality_storage)
  gtagUpdate({ 'functionality_storage': prefs.functional ? 'granted' : 'denied' });

  // Analytics
  if (prefs.analytics) enableAnalytics();
  else disableAnalytics();

  // Marketing
  if (prefs.marketing) enableMarketing();
  else disableMarketing();
}

export function initConsentEffects(getCurrent?: () => CookiePreferences | null) {
  if (initialized) {
    // Also re-apply current state if provided
    if (getCurrent) applyConsent(getCurrent());
    return;
  }
  initialized = true;

  // Apply current state on init
  if (getCurrent) applyConsent(getCurrent());

  // Listen for changes
  window.addEventListener('cookie-consent-changed', (e: Event) => {
    try {
      const ce = e as CustomEvent<CookiePreferences>;
      applyConsent(ce.detail);
    } catch {
      // no-op
    }
  });
}
